import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import StudentPicker from "../../components/StudentPicker";
import { createLookupApi } from "../../api/lookupApi";
import { saveTransferCertificate, listTransferCertificates } from "../../api/transferCertificateApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Direct port of digicampusbd's real, heavily-used {@code tc-add}/
 * {@code tc-download} pages (433 real transfer certificates). Same
 * consolidation and Jasper-deferral pattern as {@code TestimonialPage}.
 */
export default function TransferCertificatePage() {
  const [tab, setTab] = useState("add");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Transfer Certificate</h1>
      </div>
      <Tabs
        tabs={[
          { key: "add", label: "Add / Edit" },
          { key: "list", label: "List & View" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "add" && <AddTab setError={setError} setSuccess={setSuccess} />}
      {tab === "list" && <ListTab setError={setError} />}
    </MainLayout>
  );
}

function AddTab({ setError, setSuccess }) {
  const [student, setStudent] = useState(null);
  const [tcReason, setTcReason] = useState("");
  const [leftDate, setLeftDate] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!student) {
      setError("Select a student first.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveTransferCertificate({
        tcReason,
        leftDate,
        identificationId: student.identificationId,
      });
      setSuccess(res.message);
      setTcReason("");
      setLeftDate("");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save transfer certificate.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StudentPicker onSelect={setStudent} />
      <form onSubmit={handleSubmit} className="user-form">
        <label>Reason for Leaving</label>
        <textarea rows={3} value={tcReason} onChange={(e) => setTcReason(e.target.value)} required />
        <label>Left Date</label>
        <input type="date" value={leftDate} onChange={(e) => setLeftDate(e.target.value)} required />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving || !student}>
            {saving ? "Saving..." : "Save Transfer Certificate"}
          </button>
        </div>
      </form>
    </>
  );
}

function ListTab({ setError }) {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    if (!academicYear) return;
    setLoading(true);
    listTransferCertificates(academicYear)
      .then(setRows)
      .catch(() => setError("Failed to load transfer certificates."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear]);

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "tcSerial", label: "Serial" },
            { key: "studentName", label: "Student" },
            { key: "className", label: "Class" },
            { key: "tcReason", label: "Reason" },
            { key: "leftDate", label: "Left Date" },
            { key: "tcIssueDate", label: "Issue Date" },
            { key: "executeUser", label: "Issued By" },
          ]}
          rows={rows}
          actions={(row) => (
            <button type="button" className="btn-link" onClick={() => setViewing(row)}>
              View
            </button>
          )}
        />
      )}

      {viewing && (
        <div style={{ marginTop: 16, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, padding: "1.5rem" }}>
          <div className="page-header">
            <h3>Transfer Certificate — {viewing.tcSerial}</h3>
            <button type="button" className="btn-link" onClick={() => setViewing(null)}>
              Close
            </button>
          </div>
          <div className="table-scroll">
          <table className="attendance-table">
            <tbody>
              <tr>
                <th>Student Name</th>
                <td>{viewing.studentName}</td>
              </tr>
              <tr>
                <th>Father Name</th>
                <td>{viewing.fatherName}</td>
              </tr>
              <tr>
                <th>Mother Name</th>
                <td>{viewing.motherName}</td>
              </tr>
              <tr>
                <th>Class / Group</th>
                <td>
                  {viewing.className} / {viewing.groupName}
                </td>
              </tr>
              <tr>
                <th>Section / Shift</th>
                <td>
                  {viewing.sectionName} / {viewing.shiftName}
                </td>
              </tr>
              <tr>
                <th>Address</th>
                <td>
                  {viewing.postOffice}, {viewing.thana}, {viewing.zilla}
                </td>
              </tr>
              <tr>
                <th>Reason</th>
                <td>{viewing.tcReason}</td>
              </tr>
              <tr>
                <th>Left Date</th>
                <td>{viewing.leftDate}</td>
              </tr>
              <tr>
                <th>Issue Date</th>
                <td>{viewing.tcIssueDate}</td>
              </tr>
              <tr>
                <th>Issued By</th>
                <td>{viewing.executeUser}</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      )}
    </>
  );
}
